import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import posthog from 'posthog-js';
import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { Toaster } from '@/components/ui/sonner';
import { AuthProvider } from '@/contexts/AuthContext';

import { App } from './App';
import './index.css';
import { queryClient } from './lib/queryClient';

// Initialize Sentry FIRST so any failure in the rest of the boot sequence
// (telemetry, analytics) is reported instead of silently white-screening the app.
// Gate on import.meta.env.PROD so `vite dev` (MODE === 'development') never
// ships local-only errors (e.g. PGRST002 supabase restart) to the prod project.
// `vite build --mode staging` still has PROD === true and reports to Sentry.
const sentryDsn = __INBOUNDX_SENTRY_DSN__;
if (sentryDsn && import.meta.env.PROD) {
    Sentry.init({
        dsn: sentryDsn,
        release: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
        environment: import.meta.env.MODE,
        sampleRate: 1.0,
        tracesSampleRate: 0,
        integrations: (defaults) =>
            defaults.filter(
                (i) => !i.name.toLowerCase().includes('replay') && i.name !== 'BrowserTracing'
            ),
    });
}

// Initialize PostHog analytics for client backoffice usage tracking. Best-effort:
// a failure here must never prevent the React app from mounting, so a throw is
// reported to Sentry rather than left fatal.
const POSTHOG_KEY = typeof __INBOUNDX_POSTHOG_KEY__ !== 'undefined' ? __INBOUNDX_POSTHOG_KEY__ : '';
const POSTHOG_HOST =
    typeof __INBOUNDX_POSTHOG_HOST__ !== 'undefined' ? __INBOUNDX_POSTHOG_HOST__ : '';

if (POSTHOG_KEY) {
    try {
        posthog.init(POSTHOG_KEY, {
            api_host: POSTHOG_HOST,
            capture_pageview: true,
            capture_pageleave: true,
            // Backoffice usage is tracked through explicit events; DOM autocapture was
            // 68% of this project's ingestion (10,602 of 15,547 events / 30d) and has no
            // consumer. Parity with the widget provider (IX-4541).
            autocapture: false,
            persistence: 'localStorage',
            // Features resolve from Supabase config (ConfigResolver), never PostHog —
            // so the per-load /flags fetch is pure waste and billed. Disable it (IX-3969,
            // parity with the widget posthog-provider).
            advanced_disable_feature_flags: true,
            // Session replay is off by default and started only by the onboarding gates.
            // posthog-js defaults `disable_session_recording` to false, so with replay
            // enabled on the project the recorder auto-starts at page load for EVERY
            // session (internal testers, established clients included) — the gates could
            // never scope that. Setting it true suppresses the auto-start; a gate then
            // calls startSessionRecording(), which flips this flag back on for exactly
            // the external onboarding sessions we want: the pre-claim wizard
            // (BackofficeAuthContext) and, once claimed, the in-backoffice funnel until
            // the widget goes live (OnboardingSessionReplay). Recording still requires
            // Session Replay enabled on the "Rose app" PostHog project.
            disable_session_recording: true,
            // The backoffice shows PII, so mask everything once recording runs: all
            // inputs and all rendered text — we watch the flow, not the content.
            session_recording: {
                maskAllInputs: true,
                maskTextSelector: '*',
            },
        });
    } catch (error) {
        Sentry.captureException(error);
        console.error('posthog.init failed; continuing without analytics', error);
    }
}

const ReactQueryDevtools = import.meta.env.DEV
    ? lazy(() =>
          import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools }))
      )
    : () => null;

const rootElement = document.getElementById('root');
if (!rootElement) {
    throw new Error('Root element not found');
}

// Data router (createBrowserRouter, not BrowserRouter) so descendant routes can
// use route data APIs like useBlocker. App still owns the route tree via its own
// descendant <Routes>, so a single splat route here is enough.
const router = createBrowserRouter([{ path: '/*', element: <App /> }]);

ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
        <Sentry.ErrorBoundary
            fallback={
                <div className="flex items-center justify-center min-h-screen bg-red-50">
                    <div className="text-center p-8">
                        <h1 className="text-2xl font-bold text-red-600 mb-4">
                            Something went wrong
                        </h1>
                        <p className="text-gray-600 mb-4">
                            An unexpected error occurred.
                            {sentryDsn
                                ? ' The error has been reported.'
                                : ' Please check the console for details.'}
                        </p>
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                        >
                            Reload Page
                        </button>
                    </div>
                </div>
            }
        >
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <RouterProvider router={router} />
                </AuthProvider>
                <Toaster position="top-right" />
                <Suspense fallback={null}>
                    <ReactQueryDevtools initialIsOpen={false} />
                </Suspense>
            </QueryClientProvider>
        </Sentry.ErrorBoundary>
    </React.StrictMode>
);
